import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { KYOFUKU_MEMBER_TYPE } from '../entry-points/entry';
import { ValidateState, bindStateToRadio, valid } from '../utils/entry-utils';
import { RadioContainer, RadioLabel } from './entry-form-parts';
import { GQLCouponCodeFragment, GQLTestPlacesQuery } from '../generated/graphql'
import { styled } from 'styled-components'
import { isNotNullish } from '../../../api/src/utils/type-check'
import { CouponCode } from '../../../api/src/entities/coupon-code'
import { version } from 'graphql'

const PriceTable = styled.div`
	display: grid;
	grid-template-columns: repeat(2, auto);
	border: 1px solid #ccc;
	border-radius: 0.5rem;
`

const PriceTableRow = styled.div`
	display: grid;
	grid-template-columns: subgrid;
	grid-column: span 2;
	&:not(:first-child){
		border-top: 1px solid #ccc;
	}
`

const PriceTableCell = styled.div`
	padding: 0.5rem 1.5rem;
	text-align: center;
	font-size: 0.85rem;
	&:not(:first-child){
		border-left: 1px solid #ccc;
	}
`

const TypeContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`

const TypeItem = styled.div`
	display: grid;
	grid-template-columns: 9rem 1fr;
	padding: 0.5rem;
	border: 1px solid #ccc;
	margin-left: -0.5rem;
	margin-right: -0.5rem;
	user-select: none;
	@media (max-width: 40rem){
		grid-template-columns: 1fr;
		gap: 1rem
	}
`

const TypeLeft = styled.div`
	display: grid;
	align-items: center;
`

const TypeCheckboxRow = styled.div`
	display: grid;
	grid-template-columns: 1.5rem auto;
`

const TypeCheckbox = styled.input`
	display: block;
	margin-left: -0.5rem;
`

const TypeLabel = styled.div`
	
`

const TypeContent = styled.div`
	line-height: 1.5;
	font-size: 0.8rem;
	display: none;
	margin-top: 1rem;
	margin-bottom: -0.5rem;
	&:has(+ .show) {
		display: block;
	}
`

const TypeButton = styled.div`
	display: block;
	background-color: var(--spot-color);
	color: white;
	padding: 0.5rem 1.5rem;
	width: fit-content;
	border-radius: 0.5rem;
	gap: 1rem;
	margin-top: 1rem;
	font-size: 0.85rem;
	cursor: pointer;
	text-decoration: none !important;
	span:nth-child(1){
		display: block;
	}
	span:nth-child(2){
		display: none;
	}
	&.show {
		span:nth-child(1){
			display: none;
		}
		span:nth-child(2){
			display: block;
		}
	}
`

const TypeRight = styled.div`
	margin-left: 1.5rem;
`

const TypeRightHead = styled.div`
	font-weight: 700;
	line-height: 1.5;
`

const TypeRightContent = styled.div`
	line-height: 1.5;
	font-size: 0.85rem;
	margin-top: 0.25rem;
`

const TotalPriceContainer = styled.div`
	background-color: var(--spot-color);
	color: white;
	padding: 0.75rem 1.25rem;
	margin-left: -0.5rem;
	border-radius: 0.5rem;
	margin-top: 2rem;
	line-height: 2;
	font-size: 0.85rem;
	small {
		margin-left: 0.5em;
	}
	span br {
		display: none;
	}
	span {
		font-weight: 700;
		small {
			font-size: 400;
		}
	}
	strong {
		font-size: 1rem;
		font-weight: 700;
	}

`

const switchVersion = (versions: Record<number, Course[]>, version: number | null) => {
	const v = Math.max(...Object.keys(versions).map(Number).filter(v => v <= version!))
	return versions[v]
}

// type testTypes = 'ChueiBank' | 'riebe' | 'promotion' | 'kyofuku' | 'sapporoChusho' | 'bousei' | 'insurance' | 'genovision'
type testCourse = 'standardFree' | 'standardFreeLP' | 'standardMB'

type Course = {
	id: number,
	type: testCourse,
	name: string,
	note?: ReactNode,
	disabled?: boolean
	version: number,
	prices: {
		label: string,
		value: number
	}[]
}

type SelectTestCourseData = {
	courses: Course[]
	acceptAntiCovid19Test: boolean
	availableTypes: {
		a1: boolean,
		a2: boolean
	}
}
type SelectTestCourseProps = {
	antiCovid19TestState: ValidateState<boolean | null>
	testCourseState: ValidateState<number | null>
	typeState: ValidateState<string | null>
	version: number
	testPlace: GQLTestPlacesQuery['testPlaces'][0]
	onNext: () => void,
	onPrev: () => void
}
type SelectTestCourseBaseProps = SelectTestCourseProps & {
	data: SelectTestCourseData
}

export const SelectTestCourseBase: FunctionComponent<SelectTestCourseBaseProps> = ({ onNext, onPrev, testCourseState, antiCovid19TestState, testPlace, data, typeState, version }) => {
	const [a1, setA1] = useState(false)
	const [a2, setA2] = useState(false)
	const courses = data.courses.filter(c => !c.disabled)
	const selectedCourse = courses.find(c => c.id === testCourseState[0])
	const prices = selectedCourse?.prices
	useEffect(() => {
		const types = [
			{
				a1: true,
				a2: false,
				id: 'A01'
			},
			{
				a1: false,
				a2: true,
				id: 'A02'
			},
			{
				a1: true,
				a2: true,
				id: 'B01'
			}
		]
		const type = types.find(type => type.a1 === a1 && type.a2 === a2)
		typeState[1](type?.id ?? null)
	}, [a1, a2])

	return <>
		<h3>ご希望されるメタロ・バランス検査のコースをお選びください。</h3>
		{
			courses.map((course, i) => {
				return <RadioContainer key={i}>
					<RadioLabel state={testCourseState} value={course.id}>
						<div className="course-label-container">
							<div className="course-label">
								<div className="input-outer">
									<input type="radio" {...bindStateToRadio(testCourseState, course.id)} name="test-course" />
								</div>
								<p dangerouslySetInnerHTML={{__html: course.name}} />
							</div>
						</div>
						{
							course.note
						}
					</RadioLabel>
				</RadioContainer>
			})
		}
		{
			testPlace.note && <p className="entry-form-text" style={{ whiteSpace: 'pre-wrap' }}>{testPlace.note}</p>
		}
		{
			prices && <>
				<h3>検査料金表</h3>
				<PriceTable>
					<PriceTableRow>
						<PriceTableCell>
							検査項目数
						</PriceTableCell>
						<PriceTableCell>
							検査料金​
						</PriceTableCell>
					</PriceTableRow>
						{
							prices.map((price, i) => {
								return <PriceTableRow key={i}>
									<PriceTableCell>{price.label}</PriceTableCell>
									<PriceTableCell>{price.value.toLocaleString('en-US')}円（税込）</PriceTableCell>
								</PriceTableRow>
							})
						}
				</PriceTable>
			</>
		}
		{
			selectedCourse && <>
				<h3>検査項目を選択してください。</h3>
				<TypeContainer>
					{
						testPlace.acceptA1 && (isNotNullish(data.availableTypes) && data.availableTypes.a1) && <TypeItem onClick={e => {
							if(e.target instanceof HTMLElement){
								if(!e.currentTarget.contains(e.target.closest('.c-button'))){
									const input = e.currentTarget.querySelector('input')
									if(input){
										setA1(v => !v)
									}
								}
							}
						}}>
							<TypeLeft>
								<TypeCheckboxRow>
									<TypeCheckbox type="checkbox" checked={a1} onChange={e => setA1(e.currentTarget.checked)}/>
									<TypeLabel>
										がんリスク検査
									</TypeLabel>
								</TypeCheckboxRow>
							</TypeLeft>
							<TypeRight>
								<TypeRightHead>
										複数のがんのリスクを同時に検査できます。​
								</TypeRightHead>
								<TypeRightContent>
									<div className="test-course-target-row">
										<div className="test-course-target-cell"><div>男性6種</div></div>
										<div className="test-course-target-cell"><div>大腸がん、胃がん、肺がん、すい臓がん、肝臓がん、前立腺がん</div></div>
									</div>
									<div className="test-course-target-row">
										<div className="test-course-target-cell"><div>女性9種</div></div>
										<div className="test-course-target-cell"><div>大腸がん、胃がん、肺がん、すい臓がん、肝臓がん、乳がん、子宮頸がん、子宮体がん、卵巣がん</div></div>
									</div>
								</TypeRightContent>
								<TypeContent>
									血液中の微量元素の濃度バランスを解析することにより、がんが発症している可能性（がんリスク）を判別するまったく新しいがんリスク検査です。
								</TypeContent>
								<TypeButton className='c-button' onClick={e => e.currentTarget.classList.toggle('show')}>
									<span>詳しくはこちら​</span>
									<span>とじる​</span>
								</TypeButton>
							</TypeRight>
						</TypeItem>
					}
					{
						version > 0 && testPlace.acceptA2 && (isNotNullish(data.availableTypes) && data.availableTypes.a2) && selectedCourse.version > 0 &&
						<TypeItem onClick={e => {
							if(e.target instanceof HTMLElement){
								if(!e.currentTarget.contains(e.target.closest('.c-button'))){
									const input = e.currentTarget.querySelector('input')
									if(input){
										setA2(v => !v)
									}
								}
							}
						}}>
							<TypeLeft>
								<TypeCheckboxRow>
									<TypeCheckbox type="checkbox" checked={a2} onChange={e => setA2(e.currentTarget.checked)}/>
									<TypeLabel>
										認知リスク検査​
									</TypeLabel>
								</TypeCheckboxRow>
							</TypeLeft>
							<TypeRight>
								<TypeRightHead>
								MCI（軽度認知障害）のリスク検査
								</TypeRightHead>
								<TypeRightContent>
									MCIは認知症でも無く健常でも無い状態です。<br />
									MCIを放置すると1年間で約10％が認知症に<br />
									移行すると言われています。​<br />
									早期発見・早期対処が重要です。​
								</TypeRightContent>
								<TypeContent>
									血液中の微量元素の濃度バランスを解析することにより、MCI（軽度認知障害）であるリスクを判別するまったく新しいリスク検査です。
								</TypeContent>
								<TypeButton className='c-button' onClick={e => e.currentTarget.classList.toggle('show')}>
									<span>詳しくはこちら​</span>
									<span>とじる</span>
								</TypeButton>
							</TypeRight>
						</TypeItem>
					}
				</TypeContainer>
			</>
		}

		{
			(selectedCourse && (a1 || a2)) && 
			<TotalPriceContainer>
				あなたの検査コースは <span dangerouslySetInnerHTML={{__html: selectedCourse.name ?? ''}} /> {typeState[0]}<br />
				検査料金は <strong>{selectedCourse.prices[(Number(a1) + Number(a2)) - 1].value.toLocaleString('en-US')}</strong>円（税込）です
			</TotalPriceContainer>
		}

		{testPlace.acceptAntiCovid19Test && data.acceptAntiCovid19Test && selectedCourse && (a1 || a2) && <>
			<h3>オプション検査</h3>
			<h3 style={{ marginTop: 0 }}>新型コロナウイルス抗体検査（ロシュ社）希望の有無をお選びください</h3>
			<RadioContainer>
				<RadioLabel state={antiCovid19TestState} value={true}>
					<div className="course-label-container">
						<div className="course-label long">
							<div className="input-outer">
								<input type="radio" {...bindStateToRadio(antiCovid19TestState, true)} name="anti-covid19-test" />
							</div>
							<p><small>検査オプション</small><br />新型コロナウィルス抗体検査<br /><small>（ロシュ社）</small></p>
						</div>
						<p className="price">+2,200円<small>（税込）</small></p>
					</div>
				</RadioLabel>
				<RadioLabel state={antiCovid19TestState} value={false}>
					<div className="course-label-container">
						<div className="course-label">
							<div className="input-outer">
								<input type="radio" {...bindStateToRadio(antiCovid19TestState, false)} name="anti-covid19-test" />
							</div>
							<p>希望しない</p>
						</div>
					</div>
				</RadioLabel>
			</RadioContainer>
			<p className="entry-form-text"><small>*抗体検査の結果はメタロ・バランス検査の結果と同じタイミングで書面にてお知らせいたします。（検査から5週間以内）<br />抗体の有無を一刻も早く知りたいという方には適しませんので「希望しない」を選択し、別の病院で抗体検査をお受けいただきますようお願いいたします。</small></p>
		</>}
		<div className="button-container">
			<button className="prev-button" onClick={() => {
				onPrev();
			}}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" disabled={!(valid(testCourseState) && valid(typeState) && (!testPlace.acceptAntiCovid19Test || valid(antiCovid19TestState)))} onClick={() => {
				onNext();
			}}>次へ</button>
		</div>
	</>
}

export const SelectTestCourse: FunctionComponent<SelectTestCourseProps> = props => {
	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'0': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 0,
					prices: [
						{
							label: '1つ選択',
							value: 16500
						}
					],
					disabled: !props.testPlace.acceptStandardFree
				},
				{
					id: 5,
					type: 'standardFreeLP',
					name: 'スタンダードフリー<br /><small>一般検査コース/現地決済</small>',
					version: 0,
					prices: [
						{
							label: '1つ選択',
							value: 16500
						}
					],
					disabled: !props.testPlace.acceptStandardFreeLP
				},
				{
					id: 2,
					type: 'standardMB',
					name: 'スタンダードMB<br /><small>継続研究参加コース</small>',
					version: 0,
					prices: [
						{
							label: '1つ選択',
							value: 10000
						}
					],
					note: <>
						<p className="course-detail">継続研究にご参加いただける方限定のコースです。（継続研究参加者を募集する会場・人数は随時変わりますのでご了承ください）</p>
						<p className="course-detail">検査者のがん発症の有無について追跡調査研究を行うもので、原則来年も検査を受け、かつ３年間簡単なアンケートにお答えいただける方限定のコースです。</p>
						<p className="course-detail">研究参加者管理のための「ＭＢクラブ」にご入会（入会金・年会費無料）いただきます。</p>
					</>,
					disabled: !props.testPlace.acceptStandardMb
				}
			],
			'1': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 19800
						},
						{
							label: '2つ選択',
							value: 24200
						}
					],
					disabled: !props.testPlace.acceptStandardFree
				},
				{
					id: 5,
					type: 'standardFreeLP',
					name: 'スタンダードフリー<br /><small>一般検査コース/現地決済</small>',
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 19800
						},
						{
							label: '2つ選択',
							value: 24200
						}
					],
					disabled: !props.testPlace.acceptStandardFreeLP
				},
				{
					id: 2,
					type: 'standardMB',
					name: 'スタンダードMB<br /><small>継続研究参加コース</small>',
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 13200
						},
						{
							label: '2つ選択',
							value: 16500
						}
					],
					note: <>
						<p className="course-detail">継続研究にご参加いただける方限定のコースです。（継続研究参加者を募集する会場・人数は随時変わりますのでご了承ください）</p>
						<p className="course-detail">検査者のがん発症の有無について追跡調査研究を行うもので、簡単なアンケートにお答えいただける方限定のコースです。</p>
					</>,
					disabled: !props.testPlace.acceptStandardMb
				}
			]
		}, props.version),
		acceptAntiCovid19Test: true,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}

export const SelectChueiBankTestCourse: FunctionComponent<SelectTestCourseProps> = props => {
	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'0': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 0,
					prices: [
						{
							label: '1つ選択',
							value: 0
						}
					]
				}
			],
			'1': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 0
						},
						{
							label: '2つ選択',
							value: 0
						}
					]
				}
			]
		}, props.version),
		acceptAntiCovid19Test: true,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}

export const SelectRiebeTestCourse: FunctionComponent<SelectTestCourseProps> = props => {
	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'0': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 0,
					prices: [
						{
							label: '1つ選択',
							value: 0
						}
					]
				}
			],
			'1': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 0
						},
						{
							label: '2つ選択',
							value: 0
						}
					]
				}
			]
		}, props.version),
		acceptAntiCovid19Test: true,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}		

export const SelectPromotionTestCourse: FunctionComponent<SelectTestCourseProps> = props => {
	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'0': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 0,
					prices: [
						{
							label: '1つ選択',
							value: 14300
						}
					]
				}
			],
			'1': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 17600
						},
						{
							label: '2つ選択',
							value: 22000
						}
					]
				}
			]
		}, props.version),
		
		acceptAntiCovid19Test: true,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}

export const SelectKyofukuTestCourse: FunctionComponent<SelectTestCourseProps & {kyofukuType: number | null}> = props => {
	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'0': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードMB<br /><small>継続研究参加コース</small>',
					disabled: props.kyofukuType !== KYOFUKU_MEMBER_TYPE.CURRENT,
					version: 0,
					prices: [
						{
							label: '現職会員①',
							value: 7000
						}
					]
				},
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードMB<br /><small>継続研究参加コース</small>',
					disabled: props.kyofukuType !== KYOFUKU_MEMBER_TYPE.CURRENT2,
					version: 0,
					prices: [
						{
							label: '現職会員②',
							value: 5000
						}
					]
				},
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードMB<br /><small>継続研究参加コース</small>',
					disabled: props.kyofukuType !== KYOFUKU_MEMBER_TYPE.CONTINUATION,
					version: 0,
					prices: [
						{
							label: '継続会員',
							value: 10000
						}
					]
				},
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードMB<br /><small>継続研究参加コース</small>',
					disabled: props.kyofukuType !== KYOFUKU_MEMBER_TYPE.COMPANION,
					version: 0,
					prices: [
						{
							label: '会員同行者',
							value: 10000
						}
					]
				}
			],
			'2': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードMB<br /><small>継続研究参加コース</small>',
					disabled: props.kyofukuType !== KYOFUKU_MEMBER_TYPE.CURRENT,
					version: 2,
					prices: [
						{
							label: '1つ選択',
							value: 10200
						},
						{
							label: '2つ選択',
							value: 12400
						},
					]
				},
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードMB<br /><small>継続研究参加コース</small>',
					disabled: props.kyofukuType !== KYOFUKU_MEMBER_TYPE.CURRENT2,
					version: 2,
					prices: [
						{
							label: '1つ選択',
							value: 8200
						},
						{
							label: '2つ選択',
							value: 10400
						},
					]
				},
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードMB<br /><small>継続研究参加コース</small>',
					disabled: props.kyofukuType !== KYOFUKU_MEMBER_TYPE.CONTINUATION,
					version: 2,
					prices: [
						{
							label: '1つ選択',
							value: 13200
						},
						{
							label: '2つ選択',
							value: 15400
						}
					]
				},
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードMB<br /><small>継続研究参加コース</small>',
					disabled: props.kyofukuType !== KYOFUKU_MEMBER_TYPE.COMPANION,
					version: 2,
					prices: [
						{
							label: '1つ選択',
							value: 13200
						},
						{
							label: '2つ選択',
							value: 15400
						}
					]
				}
			]
		}, props.version),
		acceptAntiCovid19Test: true,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}

export const SelectSapporoChushoTestCourse: FunctionComponent<SelectTestCourseProps> = props => {
	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'0': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 0,
					prices: [
						{
							label: '特別料金',
							value: 13500
						},
					]
				}
			],
			'2': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 15400
						},
						{
							label: '2つ選択',
							value: 19800
						}
					]
				}
			]
		}, props.version),
		acceptAntiCovid19Test: true,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}


export const SelectBohseiTestCourse: FunctionComponent<SelectTestCourseProps> = props => {
	return <SelectTestCourseBase {...props}
		data={{
			courses: switchVersion({
				'0': [
					{
						id: 4,
						type: 'standardMB',
						name: 'スタンダードMB<br /><small>法人特別コース</small>',
						version: 0,
						prices: [
							{
								label: '1つ選択',
								value: 10000
							}
						],
						disabled: !props.testPlace.acceptBohseiMb
					},
					{
						id: 5,
						type: 'standardFree',
						name: 'スタンダードフリー<br /><small>法人特別コース</small>',
						version: 0,
						prices: [
							{
								label: '1つ選択',
								value: 14300
							}
						],
						disabled: !props.testPlace.acceptBohseiFree
					}
				],
				'2': [
					{
						id: 4,
						type: 'standardMB',
						name: 'スタンダードMB<br /><small>法人特別コース</small>',
						version: 1,
						prices: [
							{
								label: '1つ選択',
								value: 11000
							},
						],
						disabled: !props.testPlace.acceptBohseiMb
					},
					{
						id: 5,
						type: 'standardFree',
						name: 'スタンダードフリー<br /><small>法人特別コース</small>',
						version: 1,
						prices: [
							{
								label: '1つ選択',
								value: 16500
							},
						],
						disabled: !props.testPlace.acceptBohseiFree
					}
				]
			}, props.version),
			acceptAntiCovid19Test: true,
			availableTypes: {
				a1: true,
				a2: false
			}
		}}
	/>
}

export const SelectInsuranceTestCourse: FunctionComponent<SelectTestCourseProps> = props => {
	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'0': [
				{
					id: 5,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 0,
					prices: [
						{
							label: '1つ選択',
							value: 15400
						}
					]
				}
			],
			'1': [
				{
					id: 5,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 18700
						},
						{
							label: '2つ選択',
							value: 23100
						}
					]
				}
			]
		}, props.version),
		acceptAntiCovid19Test: false,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}


export const SelectGenovisionTestCourse: FunctionComponent<SelectTestCourseProps & {genovisionType: number}> = props => {
	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'0': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					disabled: props.genovisionType !== 1,
					version: 0,
					prices: [
						{
							label: '1つ選択',
							value: 16500
						}
					]
				},
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					disabled: props.genovisionType !== 2,
					version: 0,
					prices: [
						{
							label: '1つ選択',
							value: 16500
						},
					]
				}
			],
			'1': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					disabled: props.genovisionType !== 1,
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 18700
						},
						{
							label: '2つ選択',
							value: 22000
						}
					]
				},
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					disabled: props.genovisionType !== 2,
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 18700
						},
						{
							label: '2つ選択',
							value: 22000
						}
					]
				}
			]
		}, props.version),
		acceptAntiCovid19Test: true,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}


export const SelectReloClubTestCourse: FunctionComponent<SelectTestCourseProps> = props => {
	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'1': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 18700
						},
						{
							label: '2つ選択',
							value: 23100
						}
					]
				}
			]
		}, props.version),
		acceptAntiCovid19Test: true,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}

export const SelectBenefitTestCourse: FunctionComponent<SelectTestCourseProps> = props => {
	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'1': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 17600
						},
						{
							label: '2つ選択',
							value: 22000
						}
					]
				}
			]
		}, props.version),
		acceptAntiCovid19Test: true,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}


export const SelectCouponCodeCourse: FunctionComponent<SelectTestCourseProps & {couponCode: GQLCouponCodeFragment}> = props => {
	const priceMap = {
		'2500': [
			{
				label: '1つ選択',
				value: 18700
			},
			{
				label: '2つ選択',
				value: 23100
			}
		],
		'2100': [
			{
				label: '1つ選択',
				value: 17600
			},
			{
				label: '2つ選択',
				value: 22000
			}
		],
		'2600': [
			{
				label: '1つ選択',
				value: 16500
			},
			{
				label: '2つ選択',
				value: 20900
			}
		],
		'2700': [
			{
				label: '1つ選択',
				value: 15400
			},
			{
				label: '2つ選択',
				value: 19800
			}
		],
		'1100': [
			{
				label: '1つ選択',
				value: 19800
			},
			{
				label: '2つ選択',
				value: 24200
			}
		],
		'2800': [
			{
				label: '1つ選択',
				value: 19250
			},
			{
				label: '2つ選択',
				value: 23650
			}
		]
	}
	const pricemapMb = {
		'8100': [
			{
				label: '1つ選択',
				value: 13200
			},
			{
				label: '2つ選択',
				value: 16500
			}
		],
		'8600': [
			{
				label: '1つ選択',
				value: 12650
			},
			{
				label: '2つ選択',
				value: 15950
			}
		],
		'8700': [
			{
				label: '1つ選択',
				value: 12100
			},
			{
				label: '2つ選択',
				value: 15400
			}
		],
		'8800': [
			{
				label: '1つ選択',
				value: 11000
			},
			{
				label: '2つ選択',
				value: 14300
			}
		],
		'8900': [
			{
				label: '1つ選択',
				value: 9900
			},
			{
				label: '2つ選択',
				value: 13200
			}
		],
		'9000': [
			{
				label: '1つ選択',
				value: 8800
			},
			{
				label: '2つ選択',
				value: 12100
			}
		]
	}

	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'1': [
				...(props.couponCode.courseCode ? [
					{
						id: 4,
						type: 'standardFree' as testCourse,
						name: 'スタンダードフリー<br /><small>一般検査コース</small>',
						version: 1,
						prices: priceMap[props.couponCode.courseCode]
					},
				] : []),
				...(props.couponCode.mbCourseCode ? [
					{
						id: 2,
						type: 'standardMB' as testCourse,
						name: `
							スタンダードMB<br>
							<small>研究参加コース</small><br>
							<small>
								継続研究にご参加いただける方限定のコースです。<br>
								※継続研究参加者を募集する会場・人数は随時変わりますのでご了承ください<br>
								※検査者のがん発症の有無について追跡調査研究を行うもので、簡単なアンケートにお答えいただける方限定のコースです。
							</small>
						`,
						version: 1,
						prices: pricemapMb[props.couponCode.mbCourseCode]
					}
				] : [])
			]
		}, props.version),
		acceptAntiCovid19Test: true,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}


export const SelectGoldenLoopCourse: FunctionComponent<SelectTestCourseProps> = props => {
	return <SelectTestCourseBase {...props} data={{
		courses: switchVersion({
			'1': [
				{
					id: 4,
					type: 'standardFree',
					name: 'スタンダードフリー<br /><small>一般検査コース</small>',
					version: 1,
					prices: [
						{
							label: '1つ選択',
							value: 18700
						},
						{
							label: '2つ選択',
							value: 22000
						}
					]
				}
			]
		}, props.version),
		acceptAntiCovid19Test: true,
		availableTypes: {
			a1: true,
			a2: true
		}
	}}/>
}
